import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HelperService } from '../../../../Service/helper.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AlertService } from '../../../../Service/alert.service';
import { ModalAlertasComponent } from '../Alerts/modal-alertas/modal-alertas.component';
import { NumberFormatDirectiveNoSum } from '../../../../Service/number-format-no-sum-directive';
import { NumberFormatPipe } from '../../../../Service/number-format.pipe';

@Component({
  selector: 'app-dj',
  imports: [CommonModule, FormsModule, ModalAlertasComponent, NumberFormatDirectiveNoSum, NumberFormatPipe],
  templateUrl: './dj.component.html',
  styleUrl: './dj.component.scss'
})
export class DJComponent implements OnInit {
  replaceChecked: boolean = false; // Desmarcado por defecto
  isLoading: boolean = true;
  djData: any = null;
  sujetoNombre: string = '';
  sujetoRut: string = '';
  repNombre: string = '';
  repRut: string = '';
  errorMsg: string = '';
  periodos: string[] = [];
  glosas: string[] = [];
  filas: {codigo: number, glosa: string}[] = [];
  editableData: { [periodo: string]: { [codigo: number]: number | null } } = {};
  montoReservas: number | null = null;
  maxInt4: number = 2147483647;

  constructor(
    private route: ActivatedRoute,
    private helperService: HelperService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const token = params['token'];
      console.log('Token extraído de la URL:', token);
      if (token) {
        this.getDJUUID(token);
      } else {
        this.errorMsg = 'No se encontró el token en la URL.';
        console.error(this.errorMsg);
      }
    });
  }

  getDJUUID(token: string): void {
    this.isLoading = true;
    this.helperService.getDJUUID(token).subscribe({
      next: (data) => {
        this.djData = data.codigos;
        console.log('Datos DJ obtenidos:', this.djData);
        this.sujetoNombre = data.nombre;
        this.sujetoRut = data.rut;
        this.repNombre = data.representante;
        this.repRut = data.rut_rep;
        this.periodos = Object.keys(data.codigos);
        const allFilas = this.periodos.flatMap(p => data.codigos[p].map((item: any) => ({codigo: item.codigo, glosa: item.glosa})));
        this.filas = Array.from(new Map(allFilas.map(item => [item.codigo, item])).values())
          .sort((a, b) => a.codigo - b.codigo);

        // Inicializar editableData
        this.editableData = {};
        for (const periodo of this.periodos) {
          this.editableData[periodo] = {};
          for (const item of data.codigos[periodo]) {
            this.editableData[periodo][item.codigo] = item.valor ?? 0;
          }
        }
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMsg = 'Error al obtener DJF-NR, asegúrese de que el enlace sea válido.';
        console.error(this.errorMsg, error);
      }
    });
  }

  getValor(periodo: string, codigo: number): any {
    const arr = this.djData?.[periodo];
    if (!arr) return '-';
    const found = arr.find((item: any) => item.codigo === codigo);
    return found?.valor ?? '-';
  }

  onValorChange(periodo: string, codigo: number, valor: any) {
    if (!this.editableData[periodo]) this.editableData[periodo] = {};
    this.editableData[periodo][codigo] = valor !== null && valor !== '' ? Number(valor) : null;
  }

  limitarMaximo(event: any) {
    const MAX_INT4 = 2147483647;
    console.log('Evento recibido:', event.target.value);
    let valor = Number(event.target.value.replace(/(?!^-)[^0-9+\-]/g, '').replace(/^-0+/, '-').replace(/^0+/, '')); // Eliminar caracteres no numéricos

    // Si el valor es inválido, mayor al máximo o menor que cero, restaurar el valor anterior
    if (isNaN(valor) || valor < -MAX_INT4 || valor > MAX_INT4) {
      event.target.value = this.montoReservas !== null ? new NumberFormatPipe().transform(this.montoReservas) : '';
      return;
    }

    this.montoReservas = valor;
  }
  guardarCambios() {
    this.isLoading = true;
    const formularios: any[] = [];

    // Validar montoReservas antes de enviar
    if (
      this.montoReservas === null ||
      isNaN(this.montoReservas) ||
      this.montoReservas < 0 ||
      this.montoReservas > this.maxInt4
    ) {
      this.isLoading = false;
      this.llamarAlerta(
        'error',
        `El monto de reservas debe ser un número entre 0 y ${this.maxInt4}`,
        () => {}
      );
      return;
    }

    // Validar que para cada periodo el código 1657 tenga un valor mayor a 0
    for (const periodo of this.periodos) {
      const valor1657 = this.editableData[periodo]?.[1657] ? this.editableData[periodo]?.[1657] : this.editableData[periodo]?.[8023];
      if (valor1657 === null || valor1657 === undefined || isNaN(valor1657) || valor1657 < 0) {
      this.isLoading = false;
      this.llamarAlerta(
        'error',
        `El campo con código 1657 o 8023 debe tener un valor mayor o igual a 0 en el periodo ${periodo}.`,
        () => {}
      );
      return;
      }
    }

    for (const periodo of this.periodos) {
      const nonZeroCodigos = Object.values(this.editableData[periodo])
        .filter(valor => valor !== null && valor !== undefined && Number(valor) !== 0);

      if (nonZeroCodigos.length < 5) {
        this.isLoading = false;
        this.llamarAlerta(
          'error',
          `Debe ingresar valores distintos de 0 en al menos 5 códigos para el periodo ${periodo}.`,
          () => {}
        );
        return;
      }
    }

    for (const periodo of this.periodos) {
      const campos: any[] = [];
      for (const codigo of Object.keys(this.editableData[periodo])) {
        const valor = this.editableData[periodo][Number(codigo)];
        if (valor !== null && valor !== undefined && !isNaN(valor)) {
          campos.push({
            codigo: Number(codigo),
            valor: valor.toString() // El backend espera string
          });
        }
      }
      if (campos.length > 0) {
        formularios.push({
          periodo: Number(periodo),
          campos
        });
      }
    }

    const token = this.route.snapshot.queryParams['token'];
    this.helperService.setDJUUID(token, formularios,this.montoReservas).subscribe({
      next: (resp) => {
        this.isLoading = false;
        console.log('Respuesta del backend:', resp);

        this.llamarAlerta(
          'response',
          resp.message,
          () => {
            window.location.href = '/';
          }
        );
      },
      error: (err) => {
        this.isLoading = false;
        this.llamarAlerta(
          'error',
          'Error al guardar los cambios',
          () => {}
        );
        this.replaceChecked = false;
        console.error(err);
      }
    });
  }

  llamarAlerta(
    alertType: string,
    message: string,
    confirmAction: () => void,
    cancelAction: () => void = () => {}
  ): void {
    this.alertService.showAlert(
      alertType,
      message,
      confirmAction,
      cancelAction
    );
  }

  isFormComplete(): boolean {
  for (const periodo of this.periodos) {
    for (const fila of this.filas) {
      // Solo validar si el campo es editable (existe en editableData)
      if (
        this.editableData[periodo] &&
        this.editableData[periodo][fila.codigo] !== undefined
      ) {
        const valor = this.editableData[periodo][fila.codigo];
        if (valor === null || valor === undefined) {
          return false;
        }
      }
    }
  }
  return true;
}
}
